import type { ComponentDefinition } from "../types";

export const LOAD: ComponentDefinition[] = [
  {
    id: "load-icom-m510e",
    name: "Icom IC-M510E VHF Radio",
    shortName: "IC-M510E",
    subtitle: "VHF / DSC radio with GNSS and Wi-Fi",
    category: "load",
    manufacturer: "Icom",
    model: "IC-M510E",
    image: "load-icom-m510e.jpg",
    voltage: 12,
    compatibleVoltages: [12],
    // Original IC-M510E manual, pp. 96–98, 105 (not the EVO):
    // 5 A maximum TX at 13.8 V; 10.8–15.6 V supply. Terminal ampacity
    // is unpublished; the supplied 10 A fuse is not an operating load.
    ampDraw: 5,
    width: 320,
    height: 240,
    terminals: [
      { id: "pos", label: "Red +", polarity: "positive", direction: "input", side: "left", offset: 0.3, maxAmp: 0 },
      { id: "neg", label: "Black −", polarity: "negative", direction: "input", side: "left", offset: 0.7, maxAmp: 0 },
      { id: "ground", label: "Ground", polarity: "ground", direction: "bidi", side: "bottom", offset: 0.5, maxAmp: 0 },
      { id: "nmea-in-a", label: "NMEA IN A · Yellow", polarity: "signal", direction: "input", side: "right", offset: 0.1, maxAmp: 0 },
      { id: "nmea-in-b", label: "NMEA IN B · Green", polarity: "signal", direction: "input", side: "right", offset: 0.26, maxAmp: 0 },
      { id: "nmea-out-a", label: "NMEA OUT A · White", polarity: "signal", direction: "output", side: "right", offset: 0.42, maxAmp: 0 },
      { id: "nmea-out-b", label: "NMEA OUT B · Brown", polarity: "signal", direction: "output", side: "right", offset: 0.58, maxAmp: 0 },
      { id: "speaker-plus", label: "Speaker + · Blue", polarity: "signal", direction: "output", side: "right", offset: 0.74, maxAmp: 0 },
      { id: "speaker-minus", label: "Speaker − · Black", polarity: "signal", direction: "output", side: "right", offset: 0.9, maxAmp: 0 },
      { id: "antenna", label: "VHF antenna", polarity: "signal", direction: "bidi", side: "top", offset: 0.1, maxAmp: 0 },
      { id: "gps", label: "GPS antenna", polarity: "signal", direction: "input", side: "top", offset: 0.37, maxAmp: 0 },
      { id: "commandmic", label: "COMMANDMIC", polarity: "signal", direction: "bidi", side: "top", offset: 0.64, maxAmp: 0 },
      { id: "mic", label: "Microphone", polarity: "signal", direction: "bidi", side: "top", offset: 0.9, maxAmp: 0 },
    ],
  },
  {
    id: "load-generic",
    name: "Generic Load",
    shortName: "Generic Load",
    subtitle: "Configurable 12V load",
    category: "load",
    ampDraw: 5,
    dutyHoursPerDay: 4,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 30,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 30,
      },
    ],
  },
  {
    id: "load-fridge-dometic",
    name: "Dometic CRX-50 Fridge",
    shortName: "Fridge CRX-50",
    // ~45 L usable — the "50" in CRX-50 is the model number, not the volume.
    subtitle: "Compressor fridge 45L",
    category: "load",
    compatibleVoltages: [12, 24],
    manufacturer: "Dometic",
    model: "CRX-50",
    ampDraw: 5.0,
    dutyHoursPerDay: 8,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 5,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 5,
      },
    ],
    image: "load-fridge-dometic.webp",
  },
  {
    id: "load-fridge-dometic-crx-65",
    name: "Dometic CRX-65 Fridge",
    shortName: "CRX-65",
    subtitle: "Compressor fridge 65L",
    category: "load",
    compatibleVoltages: [12, 24],
    manufacturer: "Dometic",
    model: "CRX-65",
    ampDraw: 5.5,
    dutyHoursPerDay: 8,
    width: 210,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 6,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 6,
      },
    ],
    watts: 66,
    ampSurge: 7,
    image: "load-fridge-dometic-crx-65.webp",
  },
  {
    id: "load-fridge-cfx3-35",
    name: "Dometic CFX3 35",
    shortName: "CFX3 35",
    subtitle: "Portable cooler 35L",
    category: "load",
    compatibleVoltages: [12, 24],
    manufacturer: "Dometic",
    model: "CFX3 35",
    ampDraw: 7.5,
    dutyHoursPerDay: 6,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 8,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 8,
      },
    ],
    watts: 90,
    ampSurge: 9,
    image: "load-fridge-cfx3-35.webp",
  },
  {
    id: "load-fridge-engel-mr040",
    name: "Engel MR040",
    shortName: "MR040",
    subtitle: "Swing-motor fridge",
    category: "load",
    compatibleVoltages: [12, 24],
    manufacturer: "Engel",
    model: "MR040",
    // Engel's MR040 product page: variable 0.7–2.7 A at 12 V; 2.7 A is the top.
    ampDraw: 2.7,
    dutyHoursPerDay: 8,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 5,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 5,
      },
    ],
    watts: 32,
    ampSurge: 5,
    image: "load-fridge-engel-mr040.webp",
  },
  {
    id: "load-heater-webasto",
    name: "Webasto AT2000 STC",
    shortName: "AT2000 STC",
    subtitle: "Diesel air heater 2kW",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Webasto",
    model: "Air Top 2000 STC",
    ampDraw: 2.5,
    ampSurge: 12,
    watts: 30,
    dutyHoursPerDay: 4,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 12,
      },
    ],
    image: "load-heater-webasto.webp",
  },
  {
    id: "load-heater-eber",
    name: "Eberspächer Airtronic D2",
    shortName: "Airtronic D2",
    subtitle: "Diesel air heater 2kW",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Eberspächer",
    model: "Airtronic D2",
    ampDraw: 3,
    ampSurge: 8.3,
    dutyHoursPerDay: 4,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 20,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 20,
      },
    ],
    watts: 34,
    image: "load-heater-eber.webp",
  },
  {
    id: "load-heater-eber-d4",
    name: "Eberspächer Airtronic D4",
    shortName: "Airtronic D4",
    subtitle: "Diesel air heater 4kW",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Eberspächer",
    model: "Airtronic D4",
    ampDraw: 4,
    ampSurge: 10,
    watts: 40,
    dutyHoursPerDay: 4,
    width: 230,
    height: 115,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 16,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 16,
      },
    ],
    image: "load-heater-eber-d4.webp",
  },
  {
    id: "load-water-heater-generic",
    name: "Generic 12V Water Heater",
    shortName: "Water Heater",
    subtitle: "DC immersion element",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 25,
    watts: 300,
    dutyHoursPerDay: 1.5,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 35,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 35,
      },
    ],
  },
  {
    id: "load-water-heater-ati-300",
    name: "ATI 12V Water Heater 300W",
    shortName: "ATI 300W",
    subtitle: "DC water heating element 300W",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "ATI",
    model: "12V 300W",
    ampDraw: 25,
    watts: 300,
    dutyHoursPerDay: 1.5,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 35,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 35,
      },
    ],
  },
  {
    id: "load-heater-truma-vario",
    name: "Truma VarioHeat",
    shortName: "VarioHeat",
    subtitle: "Gas blown-air heater",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Truma",
    model: "VarioHeat Comfort",
    ampDraw: 5.4,
    dutyHoursPerDay: 4,
    width: 230,
    height: 115,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 10,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 10,
      },
    ],
    watts: 65,
    image: "load-heater-truma-vario.webp",
  },
  {
    id: "load-ac-mabru-seaair-5000",
    name: "Mabru Sea Air 5000",
    shortName: "Sea Air 5k",
    subtitle: "12V DC marine AC, 5000 BTU",
    category: "load",
    manufacturer: "Mabru Power Systems",
    model: "Sea Air 5000 BTU 12V DC",
    ampDraw: 28,
    ampSurge: 45,
    watts: 336,
    dutyHoursPerDay: 4,
    width: 240,
    height: 130,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 60,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 60,
      },
    ],
    image: "load-ac-mabru-seaair-5000.webp",
  },
  {
    id: "load-ac-mabru-seaair-12000",
    name: "Mabru Sea Air 12000",
    shortName: "Sea Air 12k",
    subtitle: "12V DC marine AC, 12000 BTU",
    category: "load",
    manufacturer: "Mabru Power Systems",
    model: "Sea Air 12000 BTU 12V DC",
    ampDraw: 44,
    ampSurge: 70,
    watts: 528,
    dutyHoursPerDay: 4,
    width: 260,
    height: 140,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 150,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 150,
      },
    ],
    image: "load-ac-mabru-seaair-12000.webp",
  },
  {
    id: "load-fan-maxxair",
    name: "Maxxair Maxxfan Deluxe",
    shortName: "Maxxfan",
    subtitle: "Roof vent fan",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Maxxair",
    model: "Maxxfan Deluxe 7500K",
    // Airxcel 11E90001K (11-2017), p1: minimum 12 V DC, 5 A service.
    // No measured draw or startup surge is published in that manual.
    ampDraw: 5,
    dutyHoursPerDay: 6,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 6,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 6,
      },
    ],
    image: "load-fan-maxxair.webp",
  },
  {
    id: "load-fan",
    name: "12V Cabin Fan",
    shortName: "Fan",
    subtitle: "Cabin fan",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 1,
    dutyHoursPerDay: 4,
    width: 160,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 2,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 2,
      },
    ],
  },
  {
    id: "load-led-down",
    name: "12V LED Downlight",
    shortName: "LED Down",
    subtitle: "LED downlight 3W",
    category: "load",
    compatibleVoltages: [12],
    watts: 3,
    ampDraw: 0.25,
    dutyHoursPerDay: 4,
    width: 160,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 1,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
  },
  {
    id: "load-reading-lamp",
    name: "12V Reading/Wall Lamp",
    shortName: "Reading Lamp",
    subtitle: "LED reading lamp 3W",
    category: "load",
    compatibleVoltages: [12],
    watts: 3,
    ampDraw: 0.25,
    dutyHoursPerDay: 2,
    width: 180,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 1,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    image: "load-reading-lamp.webp",
  },
  {
    id: "load-led-strip",
    name: "12V LED Strip (5m)",
    shortName: "LED Strip",
    subtitle: "LED strip 24W",
    category: "load",
    compatibleVoltages: [12],
    watts: 24,
    ampDraw: 2,
    dutyHoursPerDay: 3,
    width: 180,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 3,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 3,
      },
    ],
  },
  {
    id: "load-nav-lanterns-led",
    name: "Lanterns (LED)",
    shortName: "Lanterns (LED)",
    subtitle: "LED nav lanterns (set)",
    category: "load",
    compatibleVoltages: [12, 24],
    watts: 6,
    ampDraw: 0.5,
    dutyHoursPerDay: 4,
    width: 200,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 2,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 2,
      },
    ],
    image: "load-nav-lanterns.webp",
  },
  {
    id: "load-nav-lanterns-incand",
    name: "Lanterns (Incandescent)",
    shortName: "Lanterns (Bulb)",
    subtitle: "Legacy bulb lanterns (set)",
    category: "load",
    compatibleVoltages: [12],
    watts: 40,
    ampDraw: 3.3,
    ampSurge: 8,
    dutyHoursPerDay: 4,
    width: 220,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 10,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 10,
      },
    ],
    image: "load-nav-lanterns.webp",
  },
  {
    id: "load-usb-double",
    name: "USB Outlet (2× A)",
    shortName: "USB-A 2x",
    subtitle: "Dual USB-A outlet",
    category: "load",
    compatibleVoltages: [12, 24],
    ampDraw: 4,
    dutyHoursPerDay: 2,
    width: 170,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 5,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 5,
      },
    ],
    image: "load-usb-double.webp",
  },
  {
    id: "load-usb-c-pd",
    name: "USB-C PD Outlet (60W)",
    shortName: "USB-C PD",
    subtitle: "USB-C PD outlet 60W",
    category: "load",
    compatibleVoltages: [12, 24],
    ampDraw: 5.5,
    watts: 60,
    dutyHoursPerDay: 2,
    width: 180,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 7,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 7,
      },
    ],
    image: "load-usb-c-pd.webp",
  },
  {
    id: "load-cig",
    name: "12V Cigarette Socket",
    shortName: "12V Socket",
    subtitle: "12V cigarette socket",
    category: "load",
    ampDraw: 10,
    dutyHoursPerDay: 1,
    width: 160,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 15,
      },
    ],
    image: "load-cig.webp",
  },
  {
    id: "load-stereo",
    name: "Stereo Head Unit",
    shortName: "Stereo",
    subtitle: "Stereo head unit",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 4,
    dutyHoursPerDay: 2,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 10,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.55,
        maxAmp: 10,
      },
      {
        id: "acc",
        label: "ACC",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.8,
        maxAmp: 1,
      },
    ],
  },
  {
    id: "load-fusion-ra770",
    name: "Fusion MS-RA770",
    shortName: "MS-RA770",
    subtitle: "Marine stereo 4×70W",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Fusion",
    model: "MS-RA770",
    ampDraw: 5,
    ampSurge: 15,
    dutyHoursPerDay: 2,
    width: 210,
    height: 105,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.55,
        maxAmp: 15,
      },
      {
        id: "acc",
        label: "ACC",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.8,
        maxAmp: 1,
      },
    ],
    image: "load-fusion-ra770.webp",
  },
  {
    id: "load-fusion-ra670",
    name: "Fusion MS-RA670",
    shortName: "MS-RA670",
    subtitle: "Marine stereo 4×70W",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Fusion",
    model: "MS-RA670",
    // Fusion MS-RA670 owner's manual: 15 A max current, 15 A fuse.
    ampDraw: 5,
    ampSurge: 15,
    dutyHoursPerDay: 2,
    width: 210,
    height: 105,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.55,
        maxAmp: 15,
      },
      {
        id: "acc",
        label: "ACC",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.8,
        maxAmp: 1,
      },
    ],
    image: "load-fusion-ra670.webp",
  },
  {
    id: "load-speakers",
    name: "Speaker Pair",
    shortName: "Speakers",
    subtitle: "Marine speaker pair",
    category: "load",
    ampDraw: 10,
    dutyHoursPerDay: 2,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "l-pos",
        label: "L+",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.2,
        maxAmp: 5,
      },
      {
        id: "l-neg",
        label: "L−",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.4,
        maxAmp: 5,
      },
      {
        id: "r-pos",
        label: "R+",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.6,
        maxAmp: 5,
      },
      {
        id: "r-neg",
        label: "R−",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.8,
        maxAmp: 5,
      },
    ],
  },
  {
    id: "load-water-pump",
    name: "Whale Universal Pump",
    shortName: "Water Pump",
    subtitle: "Fresh-water pump",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Whale",
    model: "Universal",
    ampDraw: 6,
    dutyHoursPerDay: 0.5,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 8,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 8,
      },
    ],
    image: "load-water-pump.webp",
  },
  {
    id: "load-shurflo-4008",
    name: "Shurflo Revolution 4008",
    shortName: "Revolution 4008",
    subtitle: "Fresh-water pump 3 GPM",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Shurflo",
    model: "4008-101-E65",
    // Pentair Revolution product table confirms 7.5 A maximum:
    // https://www.pentair.com/en-us/flow/shurflo/shurflo-products/shurflo-rv-applications/water-delivery-pumps/shurflo-revolution-4008-series-by-pass-pump.html
    // 4008-101-A65 [E65] data sheet rev 04/16 specifies a 10 A fuse, not surge.
    // https://www.pentair.com/content/dam/extranet/web/nam/shurflo/data-sheets/pds-4008-101-X65.pdf
    ampDraw: 7.5,
    dutyHoursPerDay: 0.5,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 10,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 10,
      },
    ],
    image: "load-shurflo-4008.webp",
  },
  {
    id: "load-seaflo-21-1-2gpm",
    name: "Seaflo 21-Series 1.2 GPM",
    shortName: "Seaflo 1.2 GPM",
    subtitle: "Fresh-water pump 1.2 GPM / 35 PSI",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Seaflo",
    model: "SFDP1-012-035-21",
    // Seaflo publishes 4.0 A for SFDP1-012-035-21 (seaflodirect.com + manual).
    ampDraw: 4,
    ampSurge: 6,
    dutyHoursPerDay: 0.5,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 6,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 6,
      },
    ],
  },
  {
    id: "load-schenker-zen-50",
    name: "Schenker ZEN 50 Watermaker",
    shortName: "ZEN 50 watermaker",
    subtitle: "Watermaker 50 L/h",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Schenker",
    model: "ZEN 50",
    ampDraw: 250 / 12,
    ampSurge: 400 / 12,
    dutyHoursPerDay: 1,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        // Modeling envelope above 400 W / 12 V peak, not a verified connector ampacity.
        maxAmp: 35,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        // Modeling envelope above 400 W / 12 V peak, not a verified connector ampacity.
        maxAmp: 35,
      },
    ],
    image: "load-schenker-zen-50.webp",
  },
  {
    id: "load-windlass",
    name: "Anchor Windlass",
    shortName: "Windlass",
    subtitle: "Anchor windlass 30A",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 30,
    ampSurge: 60,
    dutyHoursPerDay: 0.1,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 80,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 80,
      },
    ],
    image: "load-windlass.webp",
  },
  {
    id: "load-lewmar-v700",
    name: "Lewmar V700 Windlass",
    shortName: "V700 windlass",
    subtitle: "Anchor windlass 45A",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Lewmar",
    model: "V700",
    // Lewmar 65001022 issue 8, p4: 45 A normal draw, distinct from 35 A breaker.
    ampDraw: 45,
    ampSurge: 90,
    dutyHoursPerDay: 0.1,
    width: 220,
    height: 115,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 120,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 120,
      },
    ],
    watts: 320,
    image: "load-lewmar-v700.webp",
  },
  {
    id: "load-lewmar-1000",
    name: "Lewmar Pro-Series 1000 Windlass",
    shortName: "1000 windlass",
    subtitle: "Anchor windlass 50A",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Lewmar",
    model: "Pro-Series 1000",
    ampDraw: 50,
    ampSurge: 150,
    dutyHoursPerDay: 0.1,
    width: 220,
    height: 115,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 160,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 160,
      },
    ],
    watts: 700,
    image: "load-lewmar-1000.webp",
  },
  {
    id: "load-bow-thruster",
    name: "Bow Thruster",
    shortName: "Bow Thruster",
    subtitle: "Bow thruster",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 200,
    ampSurge: 350,
    dutyHoursPerDay: 0.05,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 350,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 350,
      },
    ],
  },
  {
    id: "load-side-power-sp75",
    name: "Side-Power SP75 Bow Thruster",
    shortName: "SP75",
    subtitle: "Bow thruster 75kgf",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Side-Power",
    model: "SP 75 T",
    // SP75T manual 2.0 (2002), printed p28: ordinary 12 V circuit 480 A.
    ampDraw: 480,
    dutyHoursPerDay: 0.05,
    width: 230,
    height: 125,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 480,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 480,
      },
    ],
  },
  {
    id: "load-lewmar-v2-24",
    name: "Lewmar V2 Windlass 24V",
    shortName: "V2 windlass 24V",
    subtitle: "Anchor windlass 24V 60A",
    category: "load",
    compatibleVoltages: [24],
    manufacturer: "Lewmar",
    model: "V2 (24V)",
    ampDraw: 60,
    ampSurge: 70,
    dutyHoursPerDay: 0.1,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 70,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 70,
      },
    ],
    image: "load-lewmar-v2-24.webp",
  },
  {
    id: "load-side-power-se80-24",
    name: "Sleipner SE80 Bow Thruster 24V",
    shortName: "SE80 24V",
    subtitle: "Bow thruster 24V 80kgf",
    category: "load",
    compatibleVoltages: [24],
    manufacturer: "Sleipner",
    model: "SE80/185T (24V)",
    ampDraw: 280,
    ampSurge: 400,
    dutyHoursPerDay: 0.05,
    width: 230,
    height: 125,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 400,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 400,
      },
    ],
    image: "load-side-power-se80-24.webp",
  },
  // Inverter convention: ampDraw = rated output W ÷ (nominal bank V × efficiency),
  // ampSurge the same over the datasheet's peak W. `watts` is real power, not the
  // VA headline. Divide by NOMINAL bank voltage — the larger current near
  // low-voltage cut-off belongs on the terminals' `maxAmp`. For an efficiency
  // inequality ("> 85 %") use the bound. Never back-derive efficiency from a
  // datasheet's "max DC input current": on the Samlex PST family that column is
  // the internal fuse bank's capacity, not a measurement.
  {
    id: "load-inverter-600",
    name: "Inverter 600 W",
    shortName: "Inv 600",
    subtitle: "Pure-sine inverter 600W",
    category: "load",
    watts: 600,
    // Samlex PST-600-12 spec sheet rev 0726: 600 W, 1000 W surge, > 85 % at 12 V.
    ampDraw: 59,
    ampSurge: 98,
    compatibleVoltages: [12],
    dutyHoursPerDay: 0.5,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 80,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 80,
      },
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 6,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 6,
      },
    ],
  },
  {
    id: "load-inverter-1500",
    name: "Inverter 1500 W",
    shortName: "Inv 1500",
    subtitle: "Pure-sine inverter 1500W",
    category: "load",
    watts: 1500,
    // Samlex PST-1500-12 spec sheet rev 0322: 1500 W, 3000 W surge, > 85 % at 12 V.
    ampDraw: 147,
    ampSurge: 294,
    compatibleVoltages: [12],
    dutyHoursPerDay: 0.5,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 200,
      },
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 12,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 12,
      },
    ],
  },
  {
    id: "load-inverter-2000",
    name: "Inverter 2000 W",
    shortName: "Inv 2000",
    subtitle: "Pure-sine inverter 2000W",
    category: "load",
    watts: 2000,
    // Samlex PST-2000-12 spec sheet rev 0322: 2000 W, 3500 W surge, > 85 % at 12 V.
    // The surge is 1.75× continuous, not the 2× the 1500 W and 3000 W units get.
    ampDraw: 196,
    ampSurge: 343,
    compatibleVoltages: [12],
    dutyHoursPerDay: 0.5,
    width: 225,
    height: 125,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 300,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 300,
      },
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 16,
      },
    ],
  },
  {
    id: "load-inverter-3000",
    name: "Inverter 3000 W",
    shortName: "Inv 3000",
    subtitle: "Pure-sine inverter 3000W",
    category: "load",
    watts: 3000,
    // Samlex PST-3000-12 spec sheet rev 0726: 3000 W, 6000 W surge, > 85 % at 12 V.
    ampDraw: 294,
    ampSurge: 588,
    compatibleVoltages: [12],
    dutyHoursPerDay: 0.5,
    width: 230,
    height: 130,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 400,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 400,
      },
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 25,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 25,
      },
    ],
  },
  {
    id: "load-inverter-victron-phoenix-1200",
    name: "Victron Phoenix Inverter 12/1200",
    shortName: "Phoenix 1200",
    subtitle: "Pure-sine inverter 1000W",
    category: "load",
    manufacturer: "Victron",
    // Not a "Smart" model — Victron's Inverter Smart range starts at 1600 VA.
    model: "Phoenix 12/1200 VE.Direct",
    // Victron "Inverters VE.Direct 250VA – 1600VA" datasheet, 12/1200 column:
    // 1200 VA / 1000 W, 2200 W peak, 91 % at 12 V. Victron's newer W-rated sheet
    // rates a 12/1200 at 1150 W — same model string, different product.
    watts: 1000,
    ampDraw: 92,
    compatibleVoltages: [12],
    dutyHoursPerDay: 0.5,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 150,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 150,
      },
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 8,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 8,
      },
      {
        id: "chassis",
        label: "Chassis PE",
        polarity: "ground",
        direction: "bidi",
        side: "bottom",
        offset: 0.5,
        // Match the DC planning envelope; not a verified earth-lug current rating.
        maxAmp: 150,
      },
    ],
    ampSurge: 201,
    image: "load-inverter-victron-phoenix-1200.webp",
  },
  {
    id: "load-inverter-victron-phoenix-800",
    name: "Victron Phoenix Inverter 12/800",
    shortName: "Phoenix 800",
    subtitle: "Pure-sine inverter 650W",
    category: "load",
    manufacturer: "Victron",
    model: "Phoenix 12/800 VE.Direct",
    // Victron "Inverters VE.Direct 250VA – 1600VA" datasheet, 12/800 column:
    // 800 VA / 650 W, 1500 W peak, 90 % at 12 V. Victron's newer W-rated sheet
    // rates a 12/800 at 800 W — same model string, different product.
    watts: 650,
    ampDraw: 60,
    compatibleVoltages: [12],
    dutyHoursPerDay: 0.5,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 125,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 125,
      },
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 7,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 7,
      },
      {
        id: "chassis",
        label: "Chassis PE",
        polarity: "ground",
        direction: "bidi",
        side: "bottom",
        offset: 0.5,
        // Match the DC planning envelope; not a verified earth-lug current rating.
        maxAmp: 125,
      },
    ],
    ampSurge: 139,
    image: "load-inverter-victron-phoenix-800.webp",
  },
  {
    id: "load-tv-12v",
    name: '12V TV (24")',
    shortName: 'TV 24"',
    subtitle: "12V LED television",
    category: "load",
    compatibleVoltages: [12],
    watts: 30,
    ampDraw: 2.5,
    dutyHoursPerDay: 2,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 4,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 4,
      },
    ],
  },
  {
    id: "load-laptop-charger",
    name: "Laptop Charger (12V)",
    shortName: "Laptop",
    subtitle: "Laptop charger 65W",
    category: "load",
    compatibleVoltages: [12, 24],
    watts: 65,
    ampDraw: 5.5,
    dutyHoursPerDay: 4,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 8,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 8,
      },
    ],
  },
  {
    id: "load-induction-cooktop",
    name: "Induction Cooktop (1800W)",
    shortName: "Induction",
    subtitle: "Induction cooktop 1800W",
    category: "load",
    watts: 1800,
    ampDraw: 155,
    dutyHoursPerDay: 0.5,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 200,
      },
    ],
  },
  {
    id: "load-microwave-700",
    // Keep saved battery-side consumption estimates electrically unchanged.
    // New AC drawings use load-microwave-ac-120 / load-microwave-ac-230.
    name: "Microwave DC Consumption Estimate (Legacy)",
    shortName: "Microwave DC estimate",
    subtitle: "Battery-side estimate · not an AC appliance",
    category: "load",
    watts: 1000,
    ampDraw: 90,
    dutyHoursPerDay: 0.1,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 150,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 150,
      },
    ],
  },
  {
    id: "load-microwave-12v-300",
    name: "Microwave 12V 300W",
    shortName: "Mw 12V",
    subtitle: "12V microwave (Wavebox-class) 300W cook",
    category: "load",
    compatibleVoltages: [12],
    watts: 400,
    ampDraw: 33,
    dutyHoursPerDay: 0.1,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 50,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 50,
      },
    ],
  },
  {
    id: "load-coffee-maker",
    name: "Coffee Maker (12V)",
    shortName: "Coffee 12V",
    subtitle: "12V coffee maker",
    category: "load",
    compatibleVoltages: [12],
    watts: 180,
    ampDraw: 15,
    dutyHoursPerDay: 0.2,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 20,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 20,
      },
    ],
  },
  {
    id: "load-starlink-mini",
    name: "Starlink Mini",
    shortName: "Starlink Mini",
    subtitle: "Satellite terminal",
    category: "load",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "SpaceX",
    model: "Starlink Mini",
    watts: 30,
    loadCurrentModel: "constant-power",
    voltage: 12,
    ampDraw: 2.5,
    dutyHoursPerDay: 6,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 6,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 6,
      },
    ],
    image: "load-starlink-mini.webp",
  },
  {
    id: "load-router-12v",
    name: "12V Router (LTE)",
    shortName: "LTE Router",
    subtitle: "12V LTE router",
    category: "load",
    compatibleVoltages: [12, 24],
    watts: 10,
    ampDraw: 0.8,
    alwaysOn: true,
    width: 180,
    height: 90,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 2,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 2,
      },
    ],
  },
  {
    id: "load-bilge-pump",
    name: "Bilge Pump 1100 GPH",
    shortName: "Bilge Pump",
    subtitle: "Bilge pump 1100 GPH",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 3.5,
    ampSurge: 5,
    dutyHoursPerDay: 0.05,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 6,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 6,
      },
    ],
    image: "load-bilge-pump.webp",
  },
  {
    id: "load-desalinator-generic",
    name: "Generic Desalinator",
    shortName: "Desalinator",
    subtitle: "12V watermaker",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 10,
    ampSurge: 25,
    watts: 120,
    dutyHoursPerDay: 2,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 20,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 20,
      },
    ],
  },
  {
    id: "load-trolling-motor-55",
    name: "Minn Kota Trolling 55lb",
    shortName: "Trolling 55",
    subtitle: "Trolling motor 55lb",
    category: "load",
    compatibleVoltages: [12],
    manufacturer: "Minn Kota",
    model: "Endura C2 55",
    ampDraw: 30,
    ampSurge: 50,
    dutyHoursPerDay: 1,
    width: 220,
    height: 115,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 60,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 60,
      },
    ],
    image: "load-trolling-motor-55.webp",
  },
  {
    id: "load-winch-12k",
    name: "Winch 12,000 lb",
    shortName: "Winch 12k",
    subtitle: "Recovery winch 12,000lb",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 200,
    ampSurge: 440,
    dutyHoursPerDay: 0.02,
    width: 230,
    height: 125,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 500,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 500,
      },
    ],
  },
  {
    id: "load-chartplotter",
    name: "Chartplotter (Garmin GPSMap)",
    shortName: "Chartplotter",
    subtitle: "Chartplotter",
    category: "load",
    compatibleVoltages: [12, 24],
    manufacturer: "Garmin",
    model: "GPSMap 943xsv",
    // Garmin installation manual v8, p. 20: 9x3 sonar maximum at 12 V.
    // Terminal caps use this planning current envelope, not published connector ampacity.
    ampDraw: 3.2,
    dutyHoursPerDay: 6,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 3.2,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 3.2,
      },
    ],
    image: "load-chartplotter.webp",
  },
  {
    id: "load-vhf-radio",
    name: "VHF Marine Radio",
    shortName: "VHF",
    subtitle: "VHF radio 25W",
    category: "load",
    compatibleVoltages: [12],
    ampDraw: 1,
    ampSurge: 5,
    dutyHoursPerDay: 12,
    width: 200,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 6,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 6,
      },
    ],
  },
  {
    id: "load-ais",
    name: "AIS Transponder",
    shortName: "AIS",
    subtitle: "AIS transponder",
    category: "load",
    compatibleVoltages: [12, 24],
    ampDraw: 0.3,
    alwaysOn: true,
    width: 180,
    height: 90,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 2,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 2,
      },
    ],
    ampSurge: 2.5,
  },
  {
    id: "load-autopilot",
    name: "Autopilot Drive",
    shortName: "Autopilot",
    subtitle: "Autopilot drive",
    category: "load",
    compatibleVoltages: [12, 24],
    ampDraw: 5,
    ampSurge: 15,
    dutyHoursPerDay: 4,
    width: 210,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 20,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 20,
      },
    ],
  },
  {
    id: "load-bms-monitor",
    name: "BMS Monitor (parasitic)",
    shortName: "BMS Parasitic",
    subtitle: "Parasitic load 0.05A",
    category: "load",
    compatibleVoltages: [12, 24, 48],
    ampDraw: 0.05,
    alwaysOn: true,
    width: 180,
    height: 80,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 1,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
  },
  {
    id: "load-inverter-victron-phoenix-24-3000",
    name: "Victron Phoenix 24/3000",
    shortName: "Phoenix 24/3000",
    subtitle: "Pure-sine inverter 24 V 3000 VA",
    category: "load",
    manufacturer: "Victron",
    model: "Phoenix 24/3000 Smart",
    // Victron "Inverters Smart 1600 VA – 5000 VA" datasheet, 24/3000 column:
    // 18.6–34 V input, 3000 VA / 2400 W, 6000 W peak, 94 % at 24 V.
    watts: 2400,
    ampDraw: 106,
    ampSurge: 266,
    compatibleVoltages: [24],
    dutyHoursPerDay: 0.5,
    width: 230,
    height: 130,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        // Bottom of the DC range: 2400 ÷ (18.6 × 0.94) = 137 A, so 200 A.
        side: "left",
        offset: 0.3,
        maxAmp: 200,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 200,
      },
      {
        // 2400 W at 230 VAC = 10.4 A; the screw terminals are rated 16 A.
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 16,
      },
    ],
  },
  {
    id: "load-inverter-victron-phoenix-48-3000",
    name: "Victron Phoenix 48/3000",
    shortName: "Phoenix 48/3000",
    subtitle: "Pure-sine inverter 48 V 3000 VA",
    category: "load",
    manufacturer: "Victron",
    model: "Phoenix 48/3000 Smart",
    // Victron "Inverters Smart 1600 VA – 5000 VA" datasheet, 48/3000 column:
    // 37.2–68 V input, 3000 VA / 2400 W, 6000 W peak. The efficiency row
    // 93/94/95 % maps positionally to 12/24/48 V, so 95 % at 48 V.
    watts: 2400,
    ampDraw: 53,
    ampSurge: 132,
    compatibleVoltages: [48],
    dutyHoursPerDay: 0.5,
    width: 230,
    height: 130,
    terminals: [
      {
        id: "pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        // Bottom of the DC range: 2400 ÷ (37.2 × 0.95) = 68 A.
        side: "left",
        offset: 0.3,
        maxAmp: 100,
      },
      {
        id: "neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 100,
      },
      {
        // 2400 W at 230 VAC = 10.4 A; the screw terminals are rated 16 A.
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 16,
      },
    ],
    image: "load-inverter-victron-phoenix-48-3000.webp",
  },
  // Manufacturer evidence and model boundaries: docs/research/component-manuals-common-50-2026-10-01.md.
  {
    "id": "load-inverter-renogy-p2-2000",
    "acVoltage": 115,
    "name": "Renogy P2 2000W 12V Inverter",
    "shortName": "P2 2000W",
    "subtitle": "Pure sine inverter 115V",
    "category": "load",
    "manufacturer": "Renogy",
    "model": "RNG-INVT-2000-12V-P2-US",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "watts": 2000,
    "ampDraw": 196.1,
    "width": 220,
    "height": 135,
    "terminals": [
      {
        "id": "pos",
        "label": "DC +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 250,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "neg",
        "label": "DC −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 250,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "ac-out-l",
        "label": "AC L",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 16.6,
        "side": "right",
        "offset": 0.2
      },
      {
        "id": "ac-out-n",
        "label": "AC N",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 16.6,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "ac-pe",
        "label": "GROUND",
        "polarity": "ground",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.8
      }
    ]
  },
  {
    "id": "load-heater-autoterm-air-2d",
    "name": "AUTOTERM Air 2D 12V",
    "shortName": "Air 2D 12V",
    "subtitle": "Diesel air heater 2kW",
    "manufacturer": "AUTOTERM",
    "model": "Air 2D 12V",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 25,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 25,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 2.4166666666666665,
    "watts": 29,
    "dutyHoursPerDay": 8
  },
  {
    "id": "load-heater-truma-combi-4e",
    "acVoltage": 230,
    "name": "Truma Combi 4 E",
    "shortName": "Combi 4 E",
    "subtitle": "Gas/electric space and water heater",
    "manufacturer": "Truma",
    "model": "Combi 4 E",
    "category": "load",
    "width": 220,
    "height": 150,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 10,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 10,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "ac-l",
        "label": "230V L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 7.8,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "ac-n",
        "label": "230V N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 7.8,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "ac-pe",
        "label": "PE",
        "polarity": "ground",
        "direction": "input",
        "maxAmp": 7.8,
        "side": "right",
        "offset": 0.75
      }
    ],
    "ampDraw": 6.5,
    "dutyHoursPerDay": 6
  },
  {
    "id": "load-ac-dometic-rtx-2000-12v",
    "name": "Dometic CoolAir RTX 2000 12V 9620001310",
    "shortName": "RTX 2000 12V",
    "subtitle": "Roof parking air conditioner",
    "manufacturer": "Dometic",
    "model": "RTX 2000 12V / 9620001310",
    "category": "load",
    "width": 220,
    "height": 140,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 60,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 60,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "sense-pos",
        "label": "Voltage monitor +",
        "polarity": "signal",
        "direction": "input",
        "maxAmp": 2,
        "side": "right",
        "offset": 0.35
      },
      {
        "id": "sense-neg",
        "label": "Voltage monitor −",
        "polarity": "signal",
        "direction": "input",
        "maxAmp": 2,
        "side": "right",
        "offset": 0.7
      }
    ],
    "ampDraw": 60,
    "dutyHoursPerDay": 4
  },
  {
    "id": "load-fan-fiamma-turbo-vent",
    "name": "Fiamma Turbo-Vent",
    "shortName": "Turbo-Vent",
    "subtitle": "Roof vent with reversing fan",
    "manufacturer": "Fiamma",
    "model": "Turbo-Vent",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 3.3,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 3.3,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 3,
    "watts": 36,
    "dutyHoursPerDay": 8
  },
  {
    "id": "load-fan-caframo-sirocco-ii",
    "name": "Caframo Sirocco II 7010",
    "shortName": "Sirocco II 7010",
    "subtitle": "Three-speed cabin circulation fan",
    "manufacturer": "Caframo",
    "model": "Sirocco II 7010",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0.35,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0.35,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 0.35,
    "dutyHoursPerDay": 8
  },
  {
    "id": "load-fridge-vitrifrigo-c51i-chr",
    "name": "Vitrifrigo C51i CHR 12/24V",
    "shortName": "C51i CHR",
    "subtitle": "Built-in compressor fridge 51L",
    "manufacturer": "Vitrifrigo",
    "model": "C51i CHR",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 15,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 15,
        "side": "left",
        "offset": 0.7
      }
    ]
  },
  {
    "id": "load-fridge-isotherm-cruise-65-elegance",
    "name": "Isotherm Cruise 65 Elegance",
    "shortName": "Cruise 65 Elegance",
    "subtitle": "Built-in compressor fridge 65L",
    "manufacturer": "Isotherm",
    "model": "Cruise 65 Elegance",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 6,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 6,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 6,
    "watts": 72,
    "dutyHoursPerDay": 8
  },
  {
    "id": "load-toilet-thetford-c223-cs",
    "image": "load-toilet-thetford-c223-cs.webp",
    "name": "Thetford C223-CS",
    "shortName": "C223-CS",
    "subtitle": "Cassette toilet with external flush supply",
    "manufacturer": "Thetford",
    "model": "C223-CS",
    "category": "load",
    "width": 220,
    "height": 140,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 3,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 3,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "relay-com",
        "label": "Pump relay COM",
        "polarity": "signal",
        "direction": "input",
        "maxAmp": 6,
        "side": "right",
        "offset": 0.35
      },
      {
        "id": "relay-no",
        "label": "Pump relay NO",
        "polarity": "signal",
        "direction": "input",
        "maxAmp": 6,
        "side": "right",
        "offset": 0.7
      }
    ]
  },
  {
    "id": "load-toilet-natures-head",
    "name": "Nature's Head Standard Composting Toilet",
    "shortName": "Standard",
    "subtitle": "Composting toilet ventilation fan",
    "manufacturer": "Nature's Head",
    "model": "Standard",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0.07,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0.07,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 0.07,
    "alwaysOn": true
  },
  {
    "id": "load-pump-reich-tandem",
    "name": "REICH Tandem 515-1914121R",
    "shortName": "Tandem 19L/min",
    "subtitle": "Submersible fresh-water pump",
    "manufacturer": "REICH",
    "model": "Tandem 515-1914121R",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 4.583333333333333,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 4.583333333333333,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 4.583333333333333,
    "watts": 55,
    "dutyHoursPerDay": 0.25
  },
  {
    "id": "load-step-thule-g2-400",
    "name": "Thule Slide-Out Step G2 400 301838",
    "shortName": "Slide-Out G2 400",
    "subtitle": "Electric slide-out entry step",
    "manufacturer": "Thule",
    "model": "Slide-Out Step G2 400 / 301838",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 20,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 20,
        "side": "left",
        "offset": 0.7
      }
    ]
  },
  {
    "id": "load-alarm-safe-t-alert-35-742",
    "name": "Safe-T-Alert 35-742",
    "shortName": "35-742",
    "subtitle": "Hardwired propane and CO alarm",
    "manufacturer": "Safe-T-Alert",
    "model": "35-742",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0.108,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0.108,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 0.108,
    "alwaysOn": true
  },
  {
    "id": "load-router-teltonika-rutx11",
    "name": "Teltonika RUTX11 Standard",
    "shortName": "RUTX11",
    "subtitle": "LTE router with Wi-Fi and Ethernet",
    "manufacturer": "Teltonika",
    "model": "RUTX11 standard",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24,
      48
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 1.7777777777777777,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 1.7777777777777777,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 1.3333333333333333,
    "watts": 16,
    "alwaysOn": true
  },
  {
    id: "load-usb-scanstrut-tile-20",
    name: "Scanstrut TILE Dual USB-C SC-TILE-20",
    shortName: "TILE Dual USB-C",
    subtitle: "Interior USB-C charger, black bezel",
    manufacturer: "Scanstrut",
    model: "SC-TILE-20",
    category: "load",
    image: "load-usb-scanstrut-tile-20.webp",
    width: 180,
    height: 140,
    voltage: 12,
    compatibleVoltages: [12, 24],
    // OEM-INST005362 Rev B: 10–32 V DC, maximum INPUT 6 A.
    // USB output watts do not establish DC input demand or conversion losses.
    // https://www.12voltplanet.co.uk/user/downloads/Scanstrut-TILE-20-21-installation-instructions.pdf
    ampDraw: 6,
    dutyHoursPerDay: 2,
    terminals: [
      { id: "pos", label: "+ (RED)", polarity: "positive", direction: "input", maxAmp: 0, side: "left", offset: 0.3 },
      { id: "neg", label: "− (BLACK)", polarity: "negative", direction: "input", maxAmp: 0, side: "left", offset: 0.7 },
    ],
  },
  {
    "id": "load-usb-scanstrut-flip-pro-max",
    "name": "Scanstrut Flip Pro Max SC-USB-F3",
    "shortName": "Flip Pro Max",
    "subtitle": "USB-C Power Delivery charger",
    "manufacturer": "Scanstrut",
    "model": "SC-USB-F3",
    "category": "load",
    "width": 220,
    "height": 110,
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 6,
        "side": "left",
        "offset": 0.35
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 6,
        "side": "left",
        "offset": 0.7
      }
    ],
    "ampDraw": 6,
    "dutyHoursPerDay": 2
  },
  {
    "id": "load-rule-mate-1100",
    "name": "Rule Rule-Mate 1100 RM1100B 12V",
    "shortName": "Rule-Mate 1100",
    "subtitle": "Automatic bilge pump",
    "category": "load",
    "manufacturer": "Rule",
    "model": "RM1100B",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 160,
    "terminals": [
      {
        "id": "auto",
        "label": "Auto + (brown)",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.25,
        "maxAmp": 7.5
      },
      {
        "id": "manual",
        "label": "Manual + (brown/white)",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 7.5
      },
      {
        "id": "neg",
        "label": "− (black)",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.8,
        "maxAmp": 7.5
      }
    ],
    "ampDraw": 4.8
  },
  {
    "id": "load-jabsco-parmax-hd4",
    "name": "Jabsco PARMAX HD4 Q401J-115S-3A",
    "shortName": "PARMAX HD4",
    "subtitle": "4 GPM pressure water pump",
    "category": "load",
    "manufacturer": "Jabsco",
    "model": "Q401J-115S-3A",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 15
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 15
      }
    ],
    "ampDraw": 10
  },
  {
    "id": "load-whale-gulper-220",
    "name": "Whale Gulper 220 BP1552 12V",
    "shortName": "Gulper 220",
    "subtitle": "Shower waste diaphragm pump",
    "category": "load",
    "manufacturer": "Whale",
    "model": "BP1552",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 5
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 5
      }
    ]
  },
  {
    "id": "load-jabsco-lite-flush",
    "name": "Jabsco Lite Flush 58500-0012 12V",
    "shortName": "Lite Flush",
    "subtitle": "Foot-switch marine toilet",
    "category": "load",
    "manufacturer": "Jabsco",
    "model": "58500-0012",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 25
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 25
      }
    ],
    "ampDraw": 20,
    "watts": 240
  },
  {
    "id": "load-jabsco-macerator-18590",
    "name": "Jabsco 18590-2092 12V Macerator",
    "shortName": "18590-2092",
    "subtitle": "Holding-tank discharge pump",
    "category": "load",
    "manufacturer": "Jabsco",
    "model": "18590-2092",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 25
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 25
      }
    ],
    "ampDraw": 20
  },
  {
    "id": "load-standard-horizon-gx1400gps",
    "name": "Standard Horizon GX1400GPS/E",
    "shortName": "GX1400GPS/E",
    "subtitle": "Fixed VHF and DSC radio",
    "category": "load",
    "manufacturer": "Standard Horizon",
    "model": "GX1400GPS/E",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 6
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 6
      }
    ],
    "ampDraw": 5
  },
  {
    "id": "load-em-trak-b923",
    "name": "em-trak B923 AIS Transceiver",
    "shortName": "B923",
    "subtitle": "AIS with integrated VHF splitter",
    "category": "load",
    "manufacturer": "em-trak",
    "model": "B923",
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 3
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 3
      }
    ],
    "ampDraw": 0.245,
    "ampSurge": 2.5,
    "watts": 2.95
  },
  {
    "id": "load-raymarine-ev100-wheel",
    "name": "Raymarine EV-100 Wheel T70152",
    "shortName": "EV-100 Wheel",
    "subtitle": "Wheel-drive autopilot kit",
    "category": "load",
    "manufacturer": "Raymarine",
    "model": "T70152",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "ACU supply +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 10
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 10
      }
    ]
  },
  {
    "id": "load-raymarine-quantum2",
    "name": "Raymarine Quantum 2 Q24D E70498",
    "shortName": "Quantum 2",
    "subtitle": "Doppler radar radome",
    "category": "load",
    "manufacturer": "Raymarine",
    "model": "Q24D / E70498",
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 5
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 5
      }
    ],
    "ampDraw": 1.4166666666666667,
    "watts": 17
  },
  {
    "id": "load-raymarine-axiom-plus-9",
    "name": "Raymarine Axiom+ 9 E70636",
    "shortName": "Axiom+ 9",
    "subtitle": "9-inch chartplotter",
    "category": "load",
    "manufacturer": "Raymarine",
    "model": "E70636",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 7
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 7
      }
    ],
    "ampDraw": 0.7458333333333332,
    "watts": 8.95
  },
  {
    "id": "load-bg-triton2",
    "name": "B&G Triton2 000-13294-001",
    "shortName": "Triton2",
    "subtitle": "NMEA 2000 instrument display",
    "category": "load",
    "manufacturer": "B&G",
    "model": "000-13294-001",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "nmea2000",
        "label": "NMEA 2000",
        "polarity": "signal",
        "direction": "bidi",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 0
      }
    ],
    "watts": 1.8
  },
  {
    "id": "load-hella-naviled-trio",
    "name": "Hella marine NaviLED TRIO 2LT980650-001",
    "shortName": "NaviLED TRIO",
    "subtitle": "Masthead tricolour and anchor light",
    "category": "load",
    "manufacturer": "Hella marine",
    "model": "2LT980650-001",
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 230,
    "height": 160,
    "terminals": [
      {
        "id": "nav",
        "label": "Nav + (red)",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.25,
        "maxAmp": 3
      },
      {
        "id": "anchor",
        "label": "Anchor + (blue)",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 3
      },
      {
        "id": "neg",
        "label": "− (black)",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.8,
        "maxAmp": 3
      }
    ],
    "ampDraw": 0.2916666666666667,
    "watts": 3.5
  },
  {
    "id": "load-minnkota-terrova-80",
    "image": "load-minnkota-terrova-80.webp",
    "name": "Minn Kota Terrova 80 24V 1358380",
    "shortName": "Terrova 80",
    "subtitle": "80 lb bow-mount trolling motor",
    "category": "load",
    "manufacturer": "Minn Kota",
    "model": "1358380",
    "voltage": 24,
    "compatibleVoltages": [
      24
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 60
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 60
      }
    ]
  },
  {
    "id": "load-lenco-trim-tabs-15000",
    "name": "Lenco 100 Series 9×9 Kit 15000-101",
    "shortName": "100 Series 9×9",
    "subtitle": "Twin electric trim-tab kit",
    "category": "load",
    "manufacturer": "Lenco",
    "model": "15000-101",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "Control supply +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 20
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 20
      }
    ]
  },
  {
    "id": "load-attwood-turbo-4000",
    "name": "Attwood Turbo 4000 Series II 1749-4",
    "shortName": "Turbo 4000",
    "subtitle": "4-inch bilge ventilation blower",
    "category": "load",
    "manufacturer": "Attwood",
    "model": "1749-4",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 5
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 5
      }
    ],
    "ampDraw": 2.9
  },
  {
    "id": "load-vetus-rw08a",
    "name": "VETUS RW08A 12V Wiper Motor",
    "shortName": "RW08A",
    "subtitle": "Two-speed self-parking wiper",
    "category": "load",
    "manufacturer": "VETUS",
    "model": "RW08A",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 130,
    "terminals": [
      {
        "id": "pos",
        "label": "Control supply +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.35,
        "maxAmp": 4
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 4
      }
    ],
    "ampDraw": 2.5
  },
  // Additional manufacturer-reviewed models; evidence: docs/research/component-manuals-additional-50-2026-10-01.md
  {
    "id": "load-em-trak-s100",
    "image": "load-em-trak-s100.webp",
    "name": "em-trak S100 AIS Antenna Splitter",
    "shortName": "S100",
    "subtitle": "Powered VHF/AIS antenna splitter",
    "category": "load",
    "manufacturer": "em-trak",
    "model": "S100",
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      },
      {
        "id": "antenna",
        "label": "Antenna RF",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.2,
        "maxAmp": 0
      },
      {
        "id": "vhf",
        "label": "VHF RF",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.5,
        "maxAmp": 0
      },
      {
        "id": "ais",
        "label": "AIS RF",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.8,
        "maxAmp": 0
      }
    ],
    "ampDraw": 0.15
  },
  {
    "id": "load-garmin-gsd25",
    "image": "load-garmin-gsd25.webp",
    "name": "Garmin GSD 25 Sonar Module",
    "shortName": "GSD 25",
    "subtitle": "Networked CHIRP sonar module",
    "category": "load",
    "manufacturer": "Garmin",
    "model": "GSD 25",
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      },
      {
        "id": "network",
        "label": "Marine Network",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.35,
        "maxAmp": 0
      },
      {
        "id": "transducer",
        "label": "Transducer",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.7,
        "maxAmp": 0
      }
    ],
    "ampDraw": 2.25,
    "watts": 27,
    "loadCurrentModel": "constant-power"
  },
  {
    "id": "load-icom-m330ge",
    "image": "load-icom-m330ge.webp",
    "name": "Icom IC-M330GE VHF Radio",
    "shortName": "IC-M330GE",
    "subtitle": "Fixed VHF with GNSS and DSC",
    "category": "load",
    "manufacturer": "Icom",
    "model": "IC-M330GE",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      },
      {
        "id": "ground",
        "label": "Ground",
        "polarity": "ground",
        "direction": "bidi",
        "side": "right",
        "offset": 0.2,
        "maxAmp": 0
      },
      {
        "id": "nmea",
        "label": "NMEA 0183",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.5,
        "maxAmp": 0
      },
      {
        "id": "antenna",
        "label": "VHF/GPS RF",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.8,
        "maxAmp": 0
      }
    ],
    "ampDraw": 5
  },
  {
    "id": "load-actisense-ngw1-iso",
    "image": "load-actisense-ngw1-iso.webp",
    "name": "Actisense NGW-1-ISO NMEA Gateway",
    "shortName": "NGW-1-ISO",
    "subtitle": "NMEA 0183 / 2000 converter",
    "category": "load",
    "manufacturer": "Actisense",
    "model": "NGW-1-ISO",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "nmea2000",
        "label": "NMEA 2000",
        "polarity": "signal",
        "direction": "bidi",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 0
      },
      {
        "id": "nmea0183",
        "label": "NMEA 0183",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.5,
        "maxAmp": 0
      }
    ]
  },
  {
    "id": "load-yachtdevices-ydnr02",
    "image": "load-yachtdevices-ydnr02.webp",
    "name": "Yacht Devices YDNR-02N Wi-Fi Router",
    "shortName": "YDNR-02N",
    "subtitle": "NMEA / SeaTalk Wi-Fi gateway",
    "category": "load",
    "manufacturer": "Yacht Devices",
    "model": "YDNR-02N",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "nmea2000",
        "label": "NMEA 2000",
        "polarity": "signal",
        "direction": "bidi",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 0
      },
      {
        "id": "nmea0183-1",
        "label": "NMEA 0183 #1",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.25,
        "maxAmp": 0
      },
      {
        "id": "nmea0183-2",
        "label": "NMEA 0183 #2",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.5,
        "maxAmp": 0
      },
      {
        "id": "seatalk",
        "label": "SeaTalk",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.75,
        "maxAmp": 0
      }
    ]
  },
  {
    "id": "load-airmar-dst810",
    "image": "load-airmar-dst810.webp",
    "name": "Airmar DST810 Smart Multisensor",
    "shortName": "DST810",
    "subtitle": "Depth, speed and temperature sensor",
    "category": "load",
    "manufacturer": "Airmar",
    "model": "DST810",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "nmea2000",
        "label": "NMEA 2000",
        "polarity": "signal",
        "direction": "bidi",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 0
      }
    ]
  },
  {
    "id": "load-rule-33ala",
    "image": "load-rule-33ala.webp",
    "name": "Rule 33ALA High Water Bilge Alarm",
    "shortName": "33ALA",
    "subtitle": "12 V high-water alarm kit",
    "category": "load",
    "manufacturer": "Rule",
    "model": "33ALA",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Alarm circuit +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Alarm circuit −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      }
    ]
  },
  {
    "id": "load-jabsco-hotshot-hd5",
    "image": "load-jabsco-hotshot-hd5.webp",
    "name": "Jabsco HotShot HD5 Washdown Pump",
    "shortName": "HotShot HD5",
    "subtitle": "5 GPM 70 PSI washdown pump",
    "category": "load",
    "manufacturer": "Jabsco",
    "model": "P501J-119N-3A",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      }
    ],
    "ampDraw": 16
  },
  {
    "id": "load-whale-gulley-ic-sg8284",
    "image": "load-whale-gulley-ic-sg8284.webp",
    "name": "Whale Gulley IC SG8284 Shower Kit",
    "shortName": "Gulley IC",
    "subtitle": "Sensor-controlled shower drain kit",
    "category": "load",
    "manufacturer": "Whale",
    "model": "SG8284 / BP2082B",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      },
      {
        "id": "indicator-override",
        "label": "White indicator / override",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "sensor",
        "label": "IC sensor plug",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.75,
        "maxAmp": 0
      }
    ],
    "ampDraw": 8
  },
  {
    "id": "load-raritan-marine-elegance-220hf012",
    "image": "load-raritan-marine-elegance-220hf012.webp",
    "name": "Raritan Marine Elegance 220HF012 Toilet",
    "shortName": "Marine Elegance",
    "subtitle": "12 V freshwater electric toilet",
    "category": "load",
    "manufacturer": "Raritan",
    "model": "220HF012",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Controller supply +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Controller return −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      }
    ],
    "ampDraw": 18
  },
  {
    "id": "load-katadyn-powersurvivor-40e",
    "image": "load-katadyn-powersurvivor-40e.webp",
    "name": "Katadyn PowerSurvivor 40E Watermaker",
    "shortName": "PowerSurvivor 40E",
    "subtitle": "12 V compact watermaker",
    "category": "load",
    "manufacturer": "Katadyn",
    "model": "PowerSurvivor 40E",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "White motor +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black motor −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      }
    ],
    "ampDraw": 4
  },
  {
    "id": "load-oceanled-x4",
    "image": "load-oceanled-x4.webp",
    "name": "OceanLED X4 Ultra White Underwater Light",
    "shortName": "X4 Ultra White",
    "subtitle": "Single-colour underwater light",
    "category": "load",
    "manufacturer": "OceanLED",
    "model": "X4 012301W",
    "voltage": 12,
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      }
    ],
    "ampDraw": 0.8,
    "watts": 10,
    "loadCurrentModel": "constant-power"
  },
  {
    "id": "load-golight-stryker-st-30004",
    "image": "load-golight-stryker-st-30004.webp",
    "name": "Golight Stryker ST 30004ST LED Searchlight",
    "shortName": "Stryker ST LED",
    "subtitle": "Wireless remote searchlight",
    "category": "load",
    "manufacturer": "Golight",
    "model": "30004ST",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Supply +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Return −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      }
    ],
    "ampDraw": 3.5
  },
  {
    "id": "load-marco-up3-oil",
    "image": "load-marco-up3-oil.webp",
    "name": "Marco UP3/OIL Oil Transfer Pump",
    "shortName": "UP3/OIL",
    "subtitle": "12 V oil transfer pump",
    "category": "load",
    "manufacturer": "Marco",
    "model": "UP3/OIL 16402012",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      }
    ],
    "ampDraw": 4.5
  },
  {
    "id": "load-bennett-bolt-brc4000",
    "image": "load-bennett-bolt-brc4000.webp",
    "name": "Bennett BOLT BRC4000 Trim Tab Control",
    "shortName": "BOLT BRC4000",
    "subtitle": "Electric trim-tab rocker control",
    "category": "load",
    "manufacturer": "Bennett Marine",
    "model": "BRC4000",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "pos",
        "label": "Orange +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "side": "left",
        "offset": 0.7,
        "maxAmp": 0
      },
      {
        "id": "port-harness",
        "label": "Port harness — red",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.3,
        "maxAmp": 0
      },
      {
        "id": "starboard-harness",
        "label": "Starboard harness — green",
        "polarity": "signal",
        "direction": "bidi",
        "side": "right",
        "offset": 0.75,
        "maxAmp": 0
      }
    ]
  },
  {
    "id": "load-heater-propex-hs2000",
    "image": "load-heater-propex-hs2000.webp",
    "name": "Propex HS2000 LPG Heater",
    "shortName": "HS2000",
    "model": "HS2000",
    "manufacturer": "Propex",
    "subtitle": "12 V LPG space heater",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Red + / CN24",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "neg",
        "label": "Black − / CN24",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "thermostat",
        "label": "Thermostat loom",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.75
      }
    ],
    "ampDraw": 1.4
  },
  {
    "id": "load-waterheater-truma-aquago-comfort",
    "image": "load-waterheater-truma-aquago-comfort.webp",
    "name": "Truma AquaGo comfort DLE60C",
    "shortName": "AquaGo comfort DLE60C",
    "model": "AquaGo comfort DLE60C",
    "manufacturer": "Truma",
    "subtitle": "12 V controls / propane hot water",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +12 V",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "control",
        "label": "Control panel",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.75
      }
    ],
    "ampDraw": 2.5
  },
  {
    "id": "load-heater-webasto-thermo-top-evo5",
    "image": "load-heater-webasto-thermo-top-evo5.webp",
    "name": "Webasto Thermo Top Evo 5 Diesel",
    "shortName": "Thermo Top Evo 5 D",
    "model": "Thermo Top Evo 5 D",
    "manufacturer": "Webasto",
    "subtitle": "12 V diesel hydronic heater",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "X2/1 +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.167
      },
      {
        "id": "neg",
        "label": "X2/2 −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "wbus",
        "label": "X1/2 W-bus",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "blower",
        "label": "X1/5 blower control",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.667
      },
      {
        "id": "fuel",
        "label": "X1/6 fuel pump",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.833
      }
    ],
    "ampDraw": 2.5833333333333335,
    "watts": 31
  },
  {
    "id": "load-fan-maxxair-dome-3810",
    "image": "load-fan-maxxair-dome-3810.webp",
    "name": "MAXXAIR MAXXFAN Dome Plus 3810",
    "shortName": "Dome Plus 3810",
    "model": "Dome Plus 3810",
    "manufacturer": "MAXXAIR",
    "subtitle": "12 V exhaust fan with LED lighting",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 160,
    "terminals": [
      {
        "id": "pos",
        "label": "FAN POS 12 VDC",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "neg",
        "label": "FAN NEG 12 VDC",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.4
      },
      {
        "id": "led-pos",
        "label": "LED POS 12 VDC",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.6
      },
      {
        "id": "led-neg",
        "label": "LED NEG 12 VDC",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.8
      }
    ]
  },
  {
    "id": "load-fan-dometic-fantastic-7350",
    "image": "load-fan-dometic-fantastic-7350.webp",
    "name": "Dometic FanTastic Vent 7350",
    "shortName": "FanTastic 7350",
    "model": "FanTastic 7350",
    "manufacturer": "Dometic",
    "subtitle": "Remote roof vent with rain sensor",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Black +12 V",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "White DC −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ],
    "ampDraw": 3,
    "watts": 36
  },
  {
    "id": "load-fridge-dometic-cd30",
    "image": "load-fridge-dometic-cd30.webp",
    "name": "Dometic CoolMatic CD30 Drawer Fridge",
    "shortName": "CoolMatic CD30",
    "model": "CoolMatic CD30",
    "manufacturer": "Dometic",
    "subtitle": "30 L drawer refrigerator",
    "category": "load",
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ],
    "ampDraw": 3,
    "watts": 36,
    "loadCurrentModel": "constant-power"
  },
  {
    "id": "load-fridge-thetford-t2090c",
    "image": "load-fridge-thetford-t2090c.webp",
    "name": "Thetford T2090C Compressor Fridge",
    "shortName": "T2090C",
    "model": "T2090C",
    "manufacturer": "Thetford",
    "subtitle": "12 V touchscreen refrigerator",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Power 1 / red +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "neg",
        "label": "Power 2 / white −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.4
      },
      {
        "id": "ci-data",
        "label": "CI 1 / data",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.6
      },
      {
        "id": "ci-unused",
        "label": "CI 2 / leave open",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.8
      }
    ]
  },
  {
    "id": "load-pump-shurflo-trail-king7",
    "image": "load-pump-shurflo-trail-king7.webp",
    "name": "SHURflo Trail King 7 Water Pump",
    "shortName": "2095-204-412",
    "model": "2095-204-412",
    "manufacturer": "SHURflo",
    "subtitle": "7 L/min demand water pump",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ],
    "ampDraw": 3.3
  },
  {
    "id": "load-pump-whale-watermaster-ep1612",
    "image": "load-pump-whale-watermaster-ep1612.webp",
    "name": "Whale Watermaster EP1612",
    "shortName": "Watermaster EP1612",
    "model": "Watermaster EP1612",
    "manufacturer": "Whale",
    "subtitle": "External submersible water pump",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Socket switched +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "Socket DC −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ],
    "ampDraw": 3.8
  },
  {
    "id": "load-alarm-linnepe-trigas",
    "image": "load-alarm-linnepe-trigas.webp",
    "name": "Linnepe TriGasAlarm 303405",
    "shortName": "TriGasAlarm 303405",
    "model": "TriGasAlarm 303405",
    "manufacturer": "Linnepe",
    "subtitle": "9–16 V LPG alarm",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Red +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "Black −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ],
    "ampDraw": 0.16
  },
  {
    "id": "load-router-peplink-br1-mini-hw3",
    "image": "load-router-peplink-br1-mini-hw3.webp",
    "name": "Peplink MAX BR1 Mini HW3",
    "shortName": "MAX BR1 Mini HW3",
    "model": "MAX BR1 Mini HW3",
    "manufacturer": "Peplink",
    "subtitle": "10–30 V cellular router",
    "category": "load",
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "DC IN +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "neg",
        "label": "DC IN −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.4
      },
      {
        "id": "ign",
        "label": "IGN I/P",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.6
      },
      {
        "id": "io",
        "label": "I/O",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.8
      }
    ],
    "ampDraw": 1.5,
    "watts": 18,
    "loadCurrentModel": "constant-power"
  },
  {
    "id": "load-light-hella-euroled75-dual",
    "image": "load-light-hella-euroled75-dual.webp",
    "name": "HELLA EuroLED 75 Red/White 12 V",
    "shortName": "2JA 015 247-011",
    "model": "2JA 015 247-011",
    "manufacturer": "HELLA marine",
    "subtitle": "Three-wire dual-colour downlight",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "white",
        "label": "White + / white light",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "red",
        "label": "Red + / red light",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "neg",
        "label": "Black common −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.75
      }
    ],
    "ampDraw": 0.25,
    "watts": 3,
    "simulateStates": {
      "default": "white",
      "options": [
        {
          "id": "white",
          "label": "White (3 W)",
          "ampOverride": 0.25
        },
        {
          "id": "red",
          "label": "Red (1 W)",
          "ampOverride": 0.08333333333333333
        },
        {
          "id": "off",
          "label": "Off",
          "ampOverride": 0
        }
      ]
    }
  },
  {
    "id": "load-light-fiamma-awning-98655-848",
    "image": "load-light-fiamma-awning-98655-848.webp",
    "name": "Fiamma LED Awning Light 98655-848",
    "shortName": "LED Awning Light 98655-848",
    "model": "LED Awning Light 98655-848",
    "manufacturer": "Fiamma",
    "subtitle": "12 V external awning light",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ],
    "ampDraw": 0.62,
    "watts": 7.4
  },
  {
    "id": "load-vent-sogii-type-b",
    "image": "load-vent-sogii-type-b.webp",
    "name": "SOG II Type B Floor Ventilation",
    "shortName": "SOG II Type B",
    "model": "SOG II Type B",
    "manufacturer": "SOG",
    "subtitle": "Thetford C200 toilet ventilation",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ]
  },
  {
    "id": "load-mover-reich-easydriver-basic",
    "image": "load-mover-reich-easydriver-basic.webp",
    "name": "REICH easydriver basic Caravan Mover",
    "shortName": "easydriver basic",
    "model": "easydriver basic",
    "manufacturer": "REICH",
    "subtitle": "12 V twin-motor caravan mover",
    "category": "load",
    "compatibleVoltages": [
      12
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "+",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "−",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ]
  },
  {
    "id": "load-tv-avtex-w215ts",
    "image": "load-tv-avtex-w215ts.webp",
    "name": "Avtex W215TS Smart TV",
    "shortName": "W215TS",
    "model": "W215TS",
    "manufacturer": "Avtex",
    "subtitle": "21.5-inch 12/24 V television",
    "category": "load",
    "compatibleVoltages": [
      12,
      24
    ],
    "width": 230,
    "height": 125,
    "terminals": [
      {
        "id": "pos",
        "label": "Centre + / DC IN",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.333
      },
      {
        "id": "neg",
        "label": "Sleeve − / DC IN",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.667
      }
    ],
    "ampDraw": 2.5,
    "watts": 30,
    "loadCurrentModel": "constant-power"
  },
];
